import {useContext, useEffect, useMemo, useState} from 'react';
import type {ComponentType} from 'react';
import {ReactRelayContext} from 'react-relay';
import RelayEnvironment from '../../../RelayEnvironment';
import {lookupProjectStages, projectStagesFromSnapshot} from '../util/ProjectStages';
import {inAdminOrder} from '../util/projectStageUtil';
import type {ProjectStage} from '../util/ProjectStages';

export type WithProjectStagesProps = {projectStages: ProjectStage[]};

/** The company's project stages in admin order, kept current with the Relay store. Never fetches. */
export const useProjectStages = (): ProjectStage[] => {
	// Modals from GenericModalConductor render outside any QueryRenderer, so fall back to the global environment.
	const environment = useContext(ReactRelayContext)?.environment ?? RelayEnvironment.getInstance();
	const [snapshot, setSnapshot] = useState(() => lookupProjectStages(environment));

	useEffect(() => {
		const current = lookupProjectStages(environment);
		setSnapshot(current);
		const disposable = environment.subscribe(current, setSnapshot);
		return () => disposable.dispose();
	}, [environment]);

	return useMemo(() => projectStagesFromSnapshot(snapshot), [snapshot]);
};

export const withProjectStages = <P extends WithProjectStagesProps>(
	Component: ComponentType<P>
): ComponentType<Omit<P, keyof WithProjectStagesProps> & Partial<WithProjectStagesProps>> => {
	const WithProjectStages = (props: Omit<P, keyof WithProjectStagesProps> & Partial<WithProjectStagesProps>) => {
		const storeStages = useProjectStages();
		// Pages outside the app shell (shared insights) have an empty store and pass their stages explicitly.
		const projectStages = props.projectStages ? inAdminOrder(props.projectStages) : storeStages;
		return <Component {...(props as unknown as P)} projectStages={projectStages} />;
	};
	WithProjectStages.displayName = `withProjectStages(${Component.displayName ?? Component.name ?? 'Component'})`;
	return WithProjectStages;
};
